<template>
  <v-container fluid class="login-screen">
    <v-row class="login-row" align="center" justify="center">
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-card class="login-card" elevation="8">
          <div class="language-links" role="group" :aria-label="$t('login.languageLabel')">
            <button
              v-for="option in languageOptions"
              :key="option.value"
              type="button"
              class="language-links__option"
              :class="{ 'language-links__option--active': locale === option.value }"
              @click="changeLocale(option.value)"
            >
              {{ option.label }}
            </button>
          </div>

          <div class="login-brand text-center">
            <v-img src="/logo.png" width="76" contain class="mx-auto mb-2" />
            <div class="login-title">
              {{ $t("login.welcomeTitle") }}
            </div>
          </div>

          <div v-if="!isPasswordReset" class="auth-switch">
            <button
              type="button"
              class="auth-switch__option"
              :class="{ 'auth-switch__option--active': !isRegister }"
              @click="selectAuthMode(0)"
            >
              {{ $t("login.signInTab") }}
            </button>
            <button
              type="button"
              class="auth-switch__option"
              :class="{ 'auth-switch__option--active': isRegister }"
              @click="selectAuthMode(1)"
            >
              {{ $t("login.registerTab") }}
            </button>
          </div>

          <v-alert
            v-if="statusMessage"
            dense
            text
            type="success"
            class="login-alert"
          >
            {{ statusMessage }}
          </v-alert>

          <div v-if="isPasswordReset" class="password-reset-copy">
            <div class="password-reset-copy__title">
              {{ $t("login.resetPasswordTitle") }}
            </div>
            <div class="password-reset-copy__text">
              {{ $t("login.resetPasswordHelp") }}
            </div>
          </div>

          <v-form ref="emailForm" v-model="emailFormValid" class="login-form" @submit.prevent="isPasswordReset ? submitPasswordReset() : submitEmailAuth()">
            <v-text-field
              v-if="isRegister && !isPasswordReset"
              v-model.trim="name"
              :label="$t('login.nameLabel')"
              outlined
              height="42"
              class="login-input"
              :rules="[rules.required]"
            />

            <v-text-field
              v-model.trim="email"
              :label="$t('login.emailLabel')"
              type="email"
              outlined
              height="42"
              class="login-input"
              autocomplete="email"
              :rules="[rules.required, rules.email]"
            />

            <v-text-field
              v-if="!isPasswordReset"
              v-model="password"
              :label="$t('login.passwordLabel')"
              :type="showPassword ? 'text' : 'password'"
              outlined
              height="42"
              class="login-input"
              :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              :autocomplete="isRegister ? 'new-password' : 'current-password'"
              :rules="[rules.required, rules.minPassword]"
              @click:append="showPassword = !showPassword"
            />

            <div v-if="!isRegister && !isPasswordReset" class="forgot-password">
              <button
                type="button"
                class="forgot-password__button"
                :disabled="emailLoading || googleLoading || resetPasswordLoading"
                @click="showPasswordReset"
              >
                {{ $t("login.forgotPassword") }}
              </button>
            </div>

            <v-btn
              color="primary"
              block
              large
              depressed
              class="login-submit"
              type="submit"
              :loading="isPasswordReset ? resetPasswordLoading : emailLoading"
              :disabled="googleLoading"
            >
              {{ submitButtonText }}
            </v-btn>

            <button
              v-if="isPasswordReset"
              type="button"
              class="back-to-login"
              :disabled="resetPasswordLoading"
              @click="hidePasswordReset"
            >
              {{ $t("login.backToSignIn") }}
            </button>
          </v-form>

          <v-checkbox
            v-if="isRegister && !isPasswordReset"
            v-model="acceptedLegal"
            class="legal-consent"
            dense
            hide-details="auto"
          >
            <template v-slot:label>
              <span class="legal-consent__text">
                {{ $t("login.acceptLegalPrefix") }}
                <router-link to="/legal-notice">{{ $t("login.legalNotice") }}</router-link>
                {{ $t("login.acceptLegalAnd") }}
                <router-link to="/privacy-policy">{{ $t("login.privacyPolicy") }}</router-link>.
              </span>
            </template>
          </v-checkbox>

          <v-btn
            color="#E44035"
            dark
            block
            large
            depressed
            class="google-submit"
            v-if="!isPasswordReset"
            @click="loginGoogle"
            :loading="googleLoading"
            :disabled="emailLoading"
          >
            {{ $t("login.googleButton") }}
          </v-btn>

          <p v-if="!isPasswordReset" class="legal-consent__text legal-consent__note">
            {{ $t("login.continueLegalNotice") }}
            <router-link to="/legal-notice">{{ $t("login.legalNotice") }}</router-link>
            {{ $t("login.continueLegalAnd") }}
            <router-link to="/privacy-policy">{{ $t("login.privacyPolicy") }}</router-link>.
          </p>

          <div class="legal-links mt-4">
            <router-link to="/legal-notice">
              {{ $t("login.legalNotice") }}
            </router-link>
            <router-link to="/privacy-policy">
              {{ $t("login.privacyPolicy") }}
            </router-link>
            <router-link to="/cookies-policy">
              {{ $t("login.cookiesPolicy") }}
            </router-link>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import i18n from "@/i18n";
import { getSupportedLocales, setAppLocale } from "@/i18n";

const FIREBASE_AUTH_ERROR_KEYS = {
  "auth/account-exists-with-different-credential": "accountExistsWithDifferentCredential",
  "auth/credential-already-in-use": "credentialAlreadyInUse",
  "auth/email-already-in-use": "emailAlreadyInUse",
  "auth/expired-action-code": "expiredActionCode",
  "auth/invalid-action-code": "invalidActionCode",
  "auth/invalid-credential": "invalidCredential",
  "auth/invalid-email": "invalidEmail",
  "auth/missing-password": "missingPassword",
  "auth/network-request-failed": "networkRequestFailed",
  "auth/operation-not-allowed": "operationNotAllowed",
  "auth/popup-blocked": "popupBlocked",
  "auth/popup-closed-by-user": "popupClosedByUser",
  "auth/requires-recent-login": "requiresRecentLogin",
  "auth/too-many-requests": "tooManyRequests",
  "auth/user-disabled": "userDisabled",
  "auth/user-not-found": "userNotFound",
  "auth/weak-password": "weakPassword",
  "auth/wrong-password": "wrongPassword"
};

export default {
  data: () => ({
    authMode: 0,
    passwordResetMode: false,
    email: "",
    emailFormValid: false,
    emailLoading: false,
    googleLoading: false,
    resetPasswordLoading: false,
    statusMessage: "",
    acceptedLegal: false,
    locale: i18n.locale,
    name: "",
    password: "",
    showPassword: false
  }),
  created() {
    if (this.$route.query.emailVerified) {
      this.statusMessage = this.$t("login.emailVerifiedNotice");
    } else if (this.$route.query.passwordReset) {
      this.statusMessage = this.$t("login.passwordResetNotice");
    }
  },
  computed: {
    isRegister() {
      return !this.isPasswordReset && this.authMode === 1;
    },
    isPasswordReset() {
      return this.passwordResetMode;
    },
    submitButtonText() {
      if (this.isPasswordReset) return this.$t("login.resetPasswordButton");
      return this.isRegister ? this.$t("login.registerButton") : this.$t("login.emailButton");
    },
    languageOptions() {
      const preferredOrder = ["es", "en", "pt"];
      const supported = getSupportedLocales();
      return preferredOrder.filter(locale => supported.includes(locale)).map(locale => ({
        value: locale,
        label: locale.toUpperCase()
      }));
    },
    rules() {
      return {
        required: value => !!value || this.$t("login.requiredField"),
        email: value =>
          /.+@.+\..+/.test(value) || this.$t("login.invalidEmail"),
        minPassword: value =>
          (value && value.length >= 6) || this.$t("login.shortPassword"),
        acceptLegal: value => !this.isRegister || !!value || this.$t("login.acceptLegalRequired")
      };
    }
  },
  methods: {
    selectAuthMode(mode) {
      this.authMode = mode;
      this.passwordResetMode = false;
      this.statusMessage = "";
    },
    showPasswordReset() {
      this.passwordResetMode = true;
      this.statusMessage = "";
      this.password = "";
      this.$nextTick(() => {
        this.$refs.emailForm?.resetValidation();
      });
    },
    hidePasswordReset() {
      this.passwordResetMode = false;
      this.statusMessage = "";
      this.$nextTick(() => {
        this.$refs.emailForm?.resetValidation();
      });
    },
    changeLocale(locale) {
      setAppLocale(locale);
      this.locale = locale;
    },
    async afterLogin() {
      await this.$store.dispatch("groups/refresh");
      await this.$store.dispatch("events/refresh");
      this.$router.replace("/calendar");
    },
    async submitEmailAuth() {
      if (!this.$refs.emailForm.validate()) return;
      if (this.isRegister && !this.acceptedLegal) {
        alert(this.$t("login.acceptLegalRequired"));
        return;
      }

      this.emailLoading = true;
      try {
        const action = this.isRegister ? "auth/registerEmail" : "auth/loginEmail";
        await this.$store.dispatch(action, {
          name: this.name,
          email: this.email,
          password: this.password,
          acceptedLegal: this.isRegister && this.acceptedLegal
        });

        if (this.isRegister) {
          this.statusMessage = this.$t("login.emailVerificationSent");
          this.authMode = 0;
          this.password = "";
          this.acceptedLegal = false;
          return;
        }

        await this.afterLogin();
      } catch (e) {
        alert(this.getAuthErrorMessage(e));
      } finally {
        this.emailLoading = false;
      }
    },
    async submitPasswordReset() {
      const requiredMessage = this.rules.required(this.email);
      if (requiredMessage !== true) {
        alert(requiredMessage);
        return;
      }

      const emailMessage = this.rules.email(this.email);
      if (emailMessage !== true) {
        alert(emailMessage);
        return;
      }

      this.resetPasswordLoading = true;
      try {
        await this.$store.dispatch("auth/resetPassword", this.email);
        this.statusMessage = this.$t("login.passwordResetSent");
      } catch (e) {
        alert(this.getAuthErrorMessage(e));
      } finally {
        this.resetPasswordLoading = false;
      }
    },
    async loginGoogle() {
      this.googleLoading = true;
      try {
        await this.$store.dispatch("auth/loginGoogle", {
          acceptedLegal: true
        });
        await this.afterLogin();
      } catch (e) {
        alert(this.getAuthErrorMessage(e));
      } finally {
        this.googleLoading = false;
      }
    },
    getAuthErrorMessage(error) {
      if (error?.code === "auth/email-not-verified") {
        return this.$t("login.emailVerificationRequired");
      }

      const errorKey = FIREBASE_AUTH_ERROR_KEYS[error?.code];
      const translationKey = errorKey ? `login.firebaseAuthErrors.${errorKey}` : "";
      if (translationKey && this.$te(translationKey)) {
        return this.$t(translationKey);
      }

      return error?.message || this.$t("login.loginError");
    }
  }
};
</script>

<style scoped>
.login-screen {
  min-height: 100%;
  background: #f7f5ff;
  padding: 16px;
}

.login-row {
  min-height: calc(100vh - 32px);
}

.login-card {
  position: relative;
  width: 100%;
  max-width: 380px;
  margin: 0 auto;
  border-radius: 16px;
  padding: 22px 26px 24px;
  box-shadow: 0 10px 22px rgba(28, 28, 42, 0.14) !important;
}

.language-links {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-bottom: 14px;
}

.language-links__option,
.auth-switch__option {
  border: 0;
  background: transparent;
  color: #5e5a66;
  cursor: pointer;
  font-family: inherit;
  font-weight: 700;
}

.language-links__option {
  min-width: 32px;
  min-height: 28px;
  padding: 0 7px;
  border-radius: 8px;
  color: #777180;
  font-size: 12px;
}

.language-links__option--active {
  background: #ede7ff;
  color: #6d28d9;
}

.login-brand {
  margin-bottom: 22px;
}

.login-title {
  color: #07070a;
  font-size: 19px;
  font-weight: 800;
  line-height: 1.2;
}

.auth-switch {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  margin-bottom: 16px;
  padding: 4px;
  border-radius: 999px;
  background: #eee7ff;
}

.auth-switch__option {
  min-height: 38px;
  border-radius: 999px;
  font-size: 14px;
}

.auth-switch__option--active {
  background: #fff;
  color: #6d28d9;
  box-shadow: 0 0 0 1px rgba(109, 40, 217, 0.08);
}

.login-form {
  margin-bottom: 10px;
}

.login-alert {
  margin-bottom: 12px;
  font-size: 12px;
}

.login-input {
  margin-bottom: 2px;
}

.forgot-password {
  display: flex;
  justify-content: flex-end;
  margin: -2px 0 10px;
}

.forgot-password__button {
  border: 0;
  background: transparent;
  color: #6d28d9;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  padding: 0;
}

.forgot-password__button:disabled {
  color: #a8a1b8;
  cursor: default;
}

.password-reset-copy {
  margin-bottom: 14px;
  text-align: center;
}

.password-reset-copy__title {
  color: #202026;
  font-size: 15px;
  font-weight: 800;
  line-height: 1.25;
}

.password-reset-copy__text {
  color: #6d6578;
  font-size: 12px;
  line-height: 1.45;
  margin-top: 6px;
}

.back-to-login {
  display: block;
  border: 0;
  background: transparent;
  color: #6d28d9;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.4;
  margin: 10px auto 0;
  padding: 0;
}

.back-to-login:disabled {
  color: #a8a1b8;
  cursor: default;
}

.login-input :deep(.v-input__slot) {
  border-radius: 10px !important;
  background: #fff !important;
}

.login-input :deep(input) {
  color: #202026;
  font-size: 14px;
}

.login-input :deep(.v-label) {
  color: #7d7d83;
  font-size: 14px;
}

.login-submit,
.google-submit {
  min-height: 44px !important;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 800;
  text-transform: none;
}

.login-submit {
  margin-top: 4px;
  background: #6d28d9 !important;
}

.google-submit {
  margin-top: 10px;
}

.legal-consent {
  align-items: flex-start;
  margin-top: 0;
  margin-bottom: 8px;
}

.legal-consent :deep(.v-input--selection-controls__input) {
  margin-right: 6px;
}

.legal-consent__text {
  color: #6d6578;
  font-size: 11px;
  line-height: 1.45;
}

.legal-consent__note {
  margin: 8px 0 0;
  text-align: center;
}

.legal-consent__text a,
.legal-links a {
  color: #6d28d9;
  font-weight: 700;
  text-decoration: none;
}

.legal-consent__text a:hover,
.legal-links a:hover {
  text-decoration: underline;
}

.legal-links {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  color: #6d6578;
  font-size: 11px;
  line-height: 1.35;
}

@media (max-width: 600px) {
  .login-screen {
    padding: 14px;
  }

  .login-row {
    min-height: calc(100vh - 28px);
  }

  .login-card {
    max-width: 340px;
    border-radius: 15px;
    padding: 20px 20px 22px;
  }

  .language-links {
    margin-bottom: 12px;
  }

  .login-brand {
    margin-bottom: 20px;
  }

  .login-title {
    font-size: 18px;
  }

  .auth-switch__option {
    min-height: 36px;
    font-size: 13px;
  }

  .login-submit,
  .google-submit {
    min-height: 42px !important;
    font-size: 14px;
  }
}
</style>
